import { ref } from "vue";
import type { FieldError } from "~/model/api/pLCnextStoreFrontendAPI.schemas";

export const useInvalidFormFieldsMixin = (fieldErrors: Ref<FieldError[]>) => {
  // Define the type of invalidFieldsLabels as an array of strings
  const invalidFieldsLabels = ref<string[]>([]);

  // Define the type of label as a string
  const modifyForUi = (
    label: string,
    fieldNames?: Record<string, string>,
  ): string => {
    // First check if the label exists in the map
    if (fieldNames && fieldNames[label]) {
      return fieldNames[label];
    }

    // Otherwise, capitalize the first letter of each word in the label
    let str = label;
    let index = str.search(/[a-z][A-Z][a-z]/);

    while (index > 0) {
      str = `${str.slice(0, index + 1)} ${str.slice(index + 1)}`;
      index = str.search(/[a-z][A-Z][a-z]/);
    }

    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Define the type of validationObject as a record with string keys
  // and values having a $invalid property which is a boolean
  const getInvalidFormFields = (
    validationObject: Record<string, { $invalid: boolean }>,
    fieldNames?: Record<string, string>,
  ): void => {
    const invalidFields: string[] = [];
    // Collect invalid fields from validationObject
    Object.entries(validationObject).forEach(([key, value]) => {
      if (value?.$invalid) {
        invalidFields.push(modifyForUi(key, fieldNames));
      }
    });
    // Collect invalid fields from fieldErrors
    const fieldErrorsLabels = fieldErrors.value
      .map((error) => error.field) // Extract the 'field' from fieldErrors
      .filter((field): field is string => Boolean(field)) // Ensure non-undefined values
      .map((field) => modifyForUi(field, fieldNames)); // Format field names

    // Merge both arrays (validationObject fields and fieldErrors fields)
    const combinedFields = [...invalidFields, ...fieldErrorsLabels];

    // Remove duplicates by using a Set
    invalidFieldsLabels.value = Array.from(new Set(combinedFields));
  };

  return {
    invalidFieldsLabels,
    getInvalidFormFields,
  };
};
